import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../lib/api";

/**
 * useLiveDrivers — real driver GPS + shift status.
 *
 * Pulls from the same canonical map-candidates endpoint TPTripsPanel uses
 * (`GET /tpt/map/candidates`). When `trip_id` is passed, returns candidates
 * ranked for that trip; without a trip id, returns all currently-online
 * drivers with their last known GPS. Off-shift drivers are excluded.
 *
 * Options:
 *   pollMs         cadence (default 8000)
 *   tripId         optional — filter to candidates for a specific trip
 *   maxAgeSec      only include drivers whose last_seen_at is within N seconds (default 120)
 */
export function useLiveDrivers({ pollMs = 8000, tripId = null, maxAgeSec = 120 } = {}) {
  const [drivers, setDrivers] = useState([]);
  const [allDrivers, setAllDrivers] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = tripId ? { trip_id: tripId } : {};
      const { data } = await apiClient.get("/tpt/map/candidates", { params });
      const rows = Array.isArray(data?.drivers) ? data.drivers : [];
      // Filter to drivers with fresh GPS (for map pins — you can't plot a
      // pin without a fresh position). Anything that just needs to CALL a
      // driver by name (command line, the dialer) should use `allDrivers`
      // instead — a driver's GPS going stale is not a reason to make them
      // uncallable.
      const cutoff = Date.now() - maxAgeSec * 1000;
      const fresh = rows.filter((d) => {
        if (d.last_seen_lat == null || d.last_seen_lng == null) return false;
        if (!d.last_seen_at) return true; // if no timestamp given, trust the coords
        const t = new Date(d.last_seen_at).getTime();
        return Number.isFinite(t) ? t >= cutoff : true;
      });
      setDrivers(fresh);
      setAllDrivers(rows);
      setFetchedAt(new Date());
    } catch (_e) { /* silent — next poll recovers */ }
  }, [tripId, maxAgeSec]);

  useEffect(() => {
    load();
    if (!pollMs) return undefined;
    const t = setInterval(load, pollMs);
    return () => clearInterval(t);
  }, [load, pollMs]);

  return { drivers, allDrivers, fetchedAt, refetch: load };
}

/** Color a driver puck by shift + load state. */
export function driverPuckColor(driver) {
  const s = String(driver?.shift_status || "").toLowerCase();
  if (s === "loaded" || s === "on_trip") return "#7fd8b7"; // green — carrying a rider
  if (s === "en_route") return "#22d3ee";
  if (s === "available" || s === "online") return "#4bb8f8";  // blue — ready
  if (s === "offline") return "#64748b";
  return "#94a3b8";
}
